/**
 * Module dependencies.
 */

import React, { forwardRef } from 'react';
import { Input as UiInput } from 'app/components/ui/input';
import { Textarea as UiTextarea } from 'app/components/ui/textarea';
import { cn } from 'app/lib/utils';
import { FormField } from '../../form-field/form-field';
import { ErrorField } from '../../error-field/error-field';
import { omit } from 'lodash';

/**
 * `InputTypeProps` type.
 */

type InputTypeProps = Omit<React.ComponentProps<'input'>, 'error'> & {
  inputType?: 'input';
};

/**
 * `TextAreaTypeProps` type.
 */

type TextAreaTypeProps = Omit<React.ComponentProps<'textarea'>, 'error'> & {
  inputType: 'textArea';
};

/**
 * `TypeProps` type.
 */

type TypeProps = InputTypeProps | TextAreaTypeProps;

/**
 * `Props` type.
 */

export type InputProps = TypeProps & {
  error: any;
  className?: string;
  fieldClassName?: string;
  invalid?: boolean;
  isDirty?: boolean;
  label: string;
  isTouched?: boolean;
  hideError?: boolean;
};

/**
 * Export `InputComponent` component.
 */

export function InputComponent(props: InputProps, forwardedRef: any): JSX.Element {
  const { className, invalid, fieldClassName, isTouched, inputType = 'input', label, hideError, ...rest } = props;
  const { error } = rest;
  const isTextArea = inputType === 'textArea';
  // `isDirty` is passed by the field controller but not a DOM attribute — drop it.
  const fieldProps = omit(rest, ['htmlType', 'isDirty']) as any;
  const fieldClassNames = cn(fieldClassName, rest.disabled && 'opacity-50');

  return (
    <FormField className={className} label={label}>
      {isTextArea ? (
        <UiTextarea className={fieldClassNames} ref={forwardedRef} {...fieldProps} />
      ) : (
        <UiInput className={fieldClassNames} ref={forwardedRef} {...fieldProps} />
      )}

      {!hideError && <ErrorField error={error} isVisible={invalid || isTouched} />}
    </FormField>
  );
}

/**
 * Export `Input` component.
 */

export const Input = forwardRef<any, InputProps>(InputComponent);
